import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(v => toCamelCase(v));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(v => toSnakeCase(v));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      result[snakeKey] = toSnakeCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

const isSchemaError = (error) => {
  return error?.code === '42P01' || error?.message?.includes('does not exist');
};

export const approvalService = {
  async getByWorkOrder(workOrderId) {
    try {
      const { data, error } = await supabase
        ?.from('quotation_approvals')
        ?.select(`
          *,
          requested_by_profile:user_profiles!quotation_approvals_requested_by_fkey(
            id,
            full_name,
            email,
            role
          ),
          manager_profile:user_profiles!quotation_approvals_manager_id_fkey(
            id,
            full_name,
            email,
            role
          ),
          work_order:work_orders(
            id,
            folio_ot,
            estado_ot
          )
        `)
        ?.eq('work_order_id', workOrderId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching approvals:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
      throw error;
    }
  },

  async getById(approvalId) {
    try {
      const { data, error } = await supabase
        ?.from('quotation_approvals')
        ?.select(`
          *,
          requested_by_profile:user_profiles!quotation_approvals_requested_by_fkey(
            id,
            full_name,
            email,
            role
          ),
          manager_profile:user_profiles!quotation_approvals_manager_id_fkey(
            id,
            full_name,
            email,
            role
          ),
          work_order:work_orders(
            id,
            folio_ot,
            estado_ot,
            client:clients(
              id,
              nombre,
              email
            )
          )
        `)
        ?.eq('id', approvalId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return null;
        console.log('Error fetching approval:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch approval:', error);
      throw error;
    }
  },

  async create(approvalData) {
    try {
      const snakeCaseData = toSnakeCase(approvalData);
      const { data, error } = await supabase
        ?.from('quotation_approvals')
        ?.insert(snakeCaseData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error creating approval:', error?.message);
        throw error;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create approval:', error);
      throw error;
    }
  },

  async update(approvalId, updateData) {
    try {
      const snakeCaseData = toSnakeCase(updateData);
      const { data, error } = await supabase
        ?.from('quotation_approvals')
        ?.update(snakeCaseData)
        ?.eq('id', approvalId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error updating approval:', error?.message);
        throw error;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update approval:', error);
      throw error;
    }
  },

  async approve(approvalId, approvalNotes, signatureData) {
    try {
      const updateData = {
        status: 'approved',
        approval_notes: approvalNotes,
        signature_data: signatureData,
        reviewed_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase
        ?.from('quotation_approvals')
        ?.update(updateData)
        ?.eq('id', approvalId)
        ?.select(`
          *,
          work_order:work_orders(
            id,
            folio_ot,
            client:clients(
              id,
              nombre,
              email
            )
          ),
          manager_profile:user_profiles!quotation_approvals_manager_id_fkey(
            id,
            full_name
          )
        `)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error approving:', error?.message);
        throw error;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to approve:', error);
      throw error;
    }
  },

  async reject(approvalId, rejectionNotes) {
    try {
      const updateData = {
        status: 'rejected',
        approval_notes: rejectionNotes,
        reviewed_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase
        ?.from('quotation_approvals')
        ?.update(updateData)
        ?.eq('id', approvalId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error rejecting:', error?.message);
        throw error;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to reject:', error);
      throw error;
    }
  },

  async sendClientNotification(approvalId) {
    try {
      // Get approval details
      const approval = await this.getById(approvalId);
      
      if (!approval) {
        throw new Error('Approval not found');
      }

      if (approval?.status !== 'approved') {
        throw new Error('Can only send notification for approved quotations');
      }

      const clientEmail = approval?.clientEmail || approval?.workOrder?.client?.email;
      const clientName = approval?.clientName || approval?.workOrder?.client?.nombre;

      if (!clientEmail) {
        throw new Error('Client email not found');
      }

      // Get Supabase URL for Edge Function
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      // Call Edge Function
      const response = await fetch(`${supabaseUrl}/functions/v1/send-approval-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          clientEmail,
          clientName,
          workOrderId: approval?.workOrder?.folioOt,
          quotationTotal: approval?.quotationTotal,
          approvedBy: approval?.managerProfile?.fullName,
          approvalNotes: approval?.approvalNotes
        })
      });

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result?.error || 'Failed to send email');
      }

      // Update approval record to mark email as sent
      await this.update(approvalId, {
        emailSent: true,
        emailSentAt: new Date()?.toISOString()
      });

      return result;
    } catch (error) {
      console.error('Failed to send client notification:', error);
      throw error;
    }
  },

  async notifySalesTeam(approvalId) {
    try {
      // Get approval details with work order info
      const approval = await this.getById(approvalId);
      
      if (!approval) {
        throw new Error('Approval not found');
      }

      if (approval?.status !== 'approved') {
        throw new Error('Can only notify sales team for approved quotations');
      }

      // Get all sales team members
      const { data: salesUsers, error: salesError } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name')
        ?.eq('role', 'ventas')
        ?.eq('is_active', true);

      if (salesError) {
        console.error('Error fetching sales team:', salesError);
        throw salesError;
      }

      if (!salesUsers || salesUsers?.length === 0) {
        console.warn('No active sales team members found');
        return { success: false, message: 'No sales team members to notify' };
      }

      const salesTeamEmails = salesUsers?.map(user => user?.email)?.filter(Boolean);

      if (salesTeamEmails?.length === 0) {
        console.warn('No valid email addresses for sales team');
        return { success: false, message: 'No valid sales team emails' };
      }

      // Get Supabase URL for Edge Function
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

      // Call Edge Function to notify sales team
      const response = await fetch(`${supabaseUrl}/functions/v1/notify-sales-team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          workOrderId: approval?.workOrder?.id,
          workOrderFolio: approval?.workOrder?.folioOt,
          quotationTotal: approval?.quotationTotal,
          approvedBy: approval?.managerProfile?.fullName,
          clientName: approval?.clientName || approval?.workOrder?.client?.nombre,
          salesTeamEmails
        })
      });

      const result = await response?.json();

      if (!response?.ok) {
        throw new Error(result?.error || 'Failed to send sales team notification');
      }

      return result;
    } catch (error) {
      console.error('Failed to notify sales team:', error);
      throw error;
    }
  },

  async getPendingApprovals(managerId) {
    try {
      const { data, error } = await supabase
        ?.from('quotation_approvals')
        ?.select(`
          *,
          requested_by_profile:user_profiles!quotation_approvals_requested_by_fkey(
            id,
            full_name,
            email
          ),
          work_order:work_orders(
            id,
            folio_ot,
            estado_ot
          )
        `)
        ?.eq('manager_id', managerId)
        ?.eq('status', 'pending')
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching pending approvals:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
      throw error;
    }
  }
};